import cookie from 'lib/cookie'
import browserDetect from 'browser-detect'
import { createContext, ReactNode, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import {
  deleteToken,
  getToken,
  isSupported,
  onMessage,
} from 'firebase/messaging'

import { messaging } from 'lib/firebase'
import { selectIsLoggedIn } from 'store/selectors'
import { useSavePushNotificationMutation } from 'store/api/yay/user'

const PushNotificationContext = createContext({
  message: undefined,
  fcmToken: null,
})

/*
If you're testing on localhost, its possible that the register token doesnt work until
you manually enable Notifications permission to your browser
*/
export const PushNotificationContextProvider = ({
  children,
}: {
  children: ReactNode
}) => {
  const [supported, setSupported] = useState(false)
  const [fcmToken, setFcmToken] = useState(null)
  const [message, setMessage] = useState()
  const isLoggedIn = useSelector(selectIsLoggedIn)

  const [savePushNotification] = useSavePushNotificationMutation()

  useEffect(() => {
    isSupported()
      .then((s) => {
        setSupported(s)
      })
      .catch(() => {
        console.log('Messaging is not supported')
      })
  }, [])

  const registerPushNotification = async () => {
    try {
      const token = await getToken(messaging())
      setFcmToken(token)

      // foreground event message
      onMessage(messaging(), (payload) => {
        const data = JSON.parse(payload.data.default)
        setMessage(data)
      })

      // background event message
      navigator.serviceWorker.addEventListener('message', ({ data }) => {
        setMessage(
          data.data
            ? JSON.parse(data.data.default)
            : JSON.parse(data['firebase-messaging-msg-data'].data.default)
        )
      })
    } catch (e) {
      console.log('Unable to get permission to notify.', e)
    }
  }

  useEffect(() => {
    if (!supported) return

    if (isLoggedIn) {
      registerPushNotification()
    } else {
      // Deletes the registration token associated with this messaging instance and unsubscribes the messaging instance from the push subscription.
      deleteToken(messaging()).catch(() => {})
    }
  }, [isLoggedIn, supported])

  useEffect(() => {
    const currentToken = cookie.pushNotification.get()
    if (currentToken !== fcmToken && fcmToken !== null) {
      // save token in cookie
      cookie.pushNotification.set(fcmToken)
      savePushNotification({
        token: fcmToken,
        browser: browserDetect().name,
      })
    }
  }, [fcmToken])

  useEffect(() => {
    if (!isLoggedIn && cookie.pushNotification.get()) {
      cookie.pushNotification.remove()
    }
  }, [isLoggedIn])

  return (
    <PushNotificationContext.Provider
      value={{
        message,
        fcmToken,
      }}
    >
      {children}
    </PushNotificationContext.Provider>
  )
}

export default PushNotificationContext
